import { isEmpty } from "../objects";
import { isUndefined } from "../identity";
import { portalEndpoints } from "portal/state/portalApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { useDebugValue } from "react";
import { useSelf } from "portal/state/store";

/**
 * Frontend source of truth for customer feature flags. Should align with backend models/customer.go
 */
export const FEATURE_FLAGS = {
  almanac: { toggleable: true },
  jobs: { toggleable: false },
  reports: { toggleable: false },
  spatial: { toggleable: false }, // on for all customers. We are no longer supporting the code paths for when this is off. DO NOT TOGGLE
  unvalidatedMetrics: { toggleable: true },
  velocityEstimator: { toggleable: true },
  categoryCollection: { toggleable: true },
  customCategories: { toggleable: true },
  entryTiming: { toggleable: true },
  scopeVeselkaChipsToCustomer: { toggleable: false }, // deprecated: all veselka requests now scope by customer id. DO NOT TOGGLE
  jigsawMetrics: { toggleable: true },
  jobsV2: { toggleable: true },
} as const;

export type FeatureFlag = keyof typeof FEATURE_FLAGS;

// there should be a corresponding entry that matches this string in the en-US.json
export const FeatureFlag = {
  REPORTS: "reports",
  ALMANAC: "almanac",
  JOBS: "jobs",
  UNVALIDATED_METRICS: "unvalidatedMetrics",
  SPATIAL: "spatial",
  TARGET_VELOCITY_ESTIMATOR: "velocityEstimator",
  CATEGORY_COLLECTION: "categoryCollection",
  CUSTOM_CATEGORIES: "customCategories",
  ENTRY_TIMING: "entryTiming",
  SCOPE_VESELKA_CHIPS_TO_CUSTOMER: "scopeVeselkaChipsToCustomer",
  JIGSAW_METRICS: "jigsawMetrics",
  JOBS_V2: "jobsV2",
} as const satisfies Record<string, FeatureFlag>;

export const toggleableByFlag: Record<FeatureFlag, boolean> =
  Object.fromEntries(
    (
      Object.entries(FEATURE_FLAGS) as [FeatureFlag, { toggleable: boolean }][]
    ).map(([k, v]) => [k, v.toggleable])
  ) as Record<FeatureFlag, boolean>;

interface FeatureFlagState {
  isEnabled: boolean | undefined;
  isLoading: boolean;
}

/**
 * Hook to get the value and loading status of a given feature flag for the current user.
 * Returns isLoading based on whether we have resolved the user state yet
 */
export const useFeatureFlag = (flag: FeatureFlag): FeatureFlagState => {
  const { user, isInternal, customer } = useSelf();

  const result: FeatureFlagState = (() => {
    // loading
    if (!user) {
      return { isEnabled: undefined, isLoading: true };
    }

    // no customer assigned
    if (isUndefined(customer) || isEmpty(customer)) {
      // default to true for internal users without a customer (which we will stop supporting soon)
      return { isEnabled: isInternal, isLoading: false };
    }

    return {
      isEnabled: Boolean(customer.featureFlags[flag]),
      isLoading: false,
    };
  })();

  let debugValue = `${flag} = ${result.isEnabled}`;
  if (result.isLoading) {
    debugValue += " (loading...)";
  }
  useDebugValue(debugValue);

  return result;
};

/**
 * Feature flag for a specific customer (support-scoped or robot owner).
 * Falls back to {@link useFeatureFlag} when `customerUuid` is missing or is the
 * logged-in user's customer.
 */
export const useFeatureFlagForCustomer = (
  flag: FeatureFlag,
  customerUuid?: string
): FeatureFlagState => {
  const selfFlag = useFeatureFlag(flag);
  const { customer } = useSelf();
  const useSelfFlag = !customerUuid || customerUuid === customer?.uuid;

  const { data: targetCustomer, isLoading: isCustomerLoading } =
    portalEndpoints.getCustomer.useQuery(
      useSelfFlag || !customerUuid ? skipToken : { customerUuid }
    );

  const result: FeatureFlagState = (() => {
    if (useSelfFlag) {
      return selfFlag;
    }
    if (isCustomerLoading) {
      return { isEnabled: undefined, isLoading: true };
    }
    return {
      isEnabled: Boolean(targetCustomer?.featureFlags[flag]),
      isLoading: false,
    };
  })();

  let debugValue = `${flag} @ ${customerUuid ?? "self"} = ${result.isEnabled}`;
  if (result.isLoading) {
    debugValue += " (loading...)";
  }
  useDebugValue(debugValue);

  return result;
};
